import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

// Tracking-script install instructions per stack (HTML, React, Next.js, Vue,
// Angular, WordPress). Extracted from the Integration page so the guided
// onboarding flow shows exactly the same snippets.

export interface IntegrationData {
  tracking_id: string;
  api_key: string;
  integration_script_url: string;
  integration_code: string;
}

export type PlatformTab = 'html' | 'react' | 'nextjs' | 'vue' | 'angular' | 'wordpress';

interface Props {
  integrationData: IntegrationData;
  initialPlatform?: PlatformTab;
}

const TrackingSnippetTabs: React.FC<Props> = ({ integrationData, initialPlatform = 'html' }) => {
  const [platformTab, setPlatformTab] = useState<PlatformTab>(initialPlatform);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  return (
                  <div className="integration-tab">
                    {/* Platform Sub-Tabs */}
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {[
                        { id: 'html', label: 'HTML', icon: 'bi-code-slash' },
                        { id: 'react', label: 'React', icon: 'bi-filetype-jsx' },
                        { id: 'nextjs', label: 'Next.js', icon: 'bi-triangle' },
                        { id: 'vue', label: 'Vue.js', icon: 'bi-hexagon' },
                        { id: 'angular', label: 'Angular', icon: 'bi-pentagon' },
                        { id: 'wordpress', label: 'WordPress', icon: 'bi-wordpress' },
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          className={`btn btn-sm ${platformTab === tab.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setPlatformTab(tab.id as PlatformTab)}
                        >
                          <i className={`bi ${tab.icon} me-1`}></i>
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* HTML Default Integration */}
                    {platformTab === 'html' && (
                      <>
                        <h5 className="mb-3">HTML Integration</h5>
                        <p className="text-muted">
                          Add the following code snippet to your website's HTML, just before the closing
                          <code className="mx-1">&lt;/head&gt;</code> tag.
                        </p>

                        <div className="mb-4">
                          <label className="form-label fw-semibold">Integration Script URL</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control font-monospace"
                              value={integrationData.integration_script_url}
                              readOnly
                            />
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => copyToClipboard(integrationData.integration_script_url, 'Script URL')}
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Integration Code</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(integrationData.integration_code, 'Integration code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <pre className="mb-0 text-light">
                              <code>{integrationData.integration_code}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="alert alert-info mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Note:</strong> After adding the code, it may take a few minutes for data to start appearing in your dashboard.
                        </div>
                      </>
                    )}

                    {/* React Integration */}
                    {platformTab === 'react' && (
                      <>
                        <h5 className="mb-3">React Integration</h5>
                        <p className="text-muted">
                          For React applications, initialize CloudAyn in your root component and track route changes.
                        </p>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 1: Add Script to index.html</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(integrationData.integration_code, 'Script code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded">
                            <pre className="mb-0 text-light">
                              <code>{integrationData.integration_code}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 2: Track Route Changes (React Router)</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(`import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function useCloudaynTracking() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    if (window.cloudayn) {
      window.cloudayn('pageview', {
        path: location.pathname,
        title: document.title
      });
    }
  }, [location]);
}

// Use in your App component:
function App() {
  useCloudaynTracking();
  return <Routes>...</Routes>;
}`, 'React hook code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            <pre className="mb-0 text-light">
                              <code>{`import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function useCloudaynTracking() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    if (window.cloudayn) {
      window.cloudayn('pageview', {
        path: location.pathname,
        title: document.title
      });
    }
  }, [location]);
}

// Use in your App component:
function App() {
  useCloudaynTracking();
  return <Routes>...</Routes>;
}`}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="alert alert-info mb-0">
                          <i className="bi bi-lightbulb me-2"></i>
                          <strong>Tip:</strong> Add TypeScript types by declaring <code>window.cloudayn</code> in a <code>.d.ts</code> file.
                        </div>
                      </>
                    )}

                    {/* Next.js Integration */}
                    {platformTab === 'nextjs' && (
                      <>
                        <h5 className="mb-3">Next.js Integration</h5>
                        <p className="text-muted">
                          For Next.js, use the Script component for optimal loading and track route changes with the router.
                        </p>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 1: Add to _app.tsx or layout.tsx</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(`// For App Router (app/layout.tsx)
import Script from 'next/script';

export default function RootLayout({ children }) {
  return (
    <html>
      <head>
        <Script
          id="cloudayn-script"
          strategy="afterInteractive"
          src="${integrationData.integration_script_url}"
        />
        <Script
          id="cloudayn-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: \`
              window.cloudayn = window.cloudayn || function() {
                (window.cloudayn.q = window.cloudayn.q || []).push(arguments);
              };
              cloudayn('init', '${integrationData.tracking_id}');
            \`
          }}
        />
      </head>
      <body>{children}</body>
    </html>
  );
}`, 'Next.js layout code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            <pre className="mb-0 text-light">
                              <code>{`// For App Router (app/layout.tsx)
import Script from 'next/script';

export default function RootLayout({ children }) {
  return (
    <html>
      <head>
        <Script
          id="cloudayn-script"
          strategy="afterInteractive"
          src="${integrationData.integration_script_url}"
        />
        <Script
          id="cloudayn-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: \`
              window.cloudayn = window.cloudayn || function() {
                (window.cloudayn.q = window.cloudayn.q || []).push(arguments);
              };
              cloudayn('init', '${integrationData.tracking_id}');
            \`
          }}
        />
      </head>
      <body>{children}</body>
    </html>
  );
}`}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 2: Track Route Changes</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(`'use client';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

export function CloudaynPageView() {
  const pathname = usePathname();

  useEffect(() => {
    if (window.cloudayn) {
      window.cloudayn('pageview', { path: pathname });
    }
  }, [pathname]);

  return null;
}

// Add <CloudaynPageView /> to your layout`, 'Next.js pageview code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded">
                            <pre className="mb-0 text-light">
                              <code>{`'use client';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

export function CloudaynPageView() {
  const pathname = usePathname();

  useEffect(() => {
    if (window.cloudayn) {
      window.cloudayn('pageview', { path: pathname });
    }
  }, [pathname]);

  return null;
}

// Add <CloudaynPageView /> to your layout`}</code>
                            </pre>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Vue.js Integration */}
                    {platformTab === 'vue' && (
                      <>
                        <h5 className="mb-3">Vue.js Integration</h5>
                        <p className="text-muted">
                          For Vue.js applications, add the script to index.html and use router guards for tracking.
                        </p>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 1: Add Script to index.html</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(integrationData.integration_code, 'Script code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded">
                            <pre className="mb-0 text-light">
                              <code>{integrationData.integration_code}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 2: Track Route Changes (Vue Router)</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(`// In your router/index.js or main.js
import router from './router';

router.afterEach((to, from) => {
  // Track page view on route change
  if (window.cloudayn) {
    window.cloudayn('pageview', {
      path: to.path,
      title: to.meta?.title || document.title
    });
  }
});`, 'Vue router code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded">
                            <pre className="mb-0 text-light">
                              <code>{`// In your router/index.js or main.js
import router from './router';

router.afterEach((to, from) => {
  // Track page view on route change
  if (window.cloudayn) {
    window.cloudayn('pageview', {
      path: to.path,
      title: to.meta?.title || document.title
    });
  }
});`}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="alert alert-info mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Vue 3 Composition API:</strong> You can also create a composable <code>useCloudayn()</code> for custom event tracking.
                        </div>
                      </>
                    )}

                    {/* Angular Integration */}
                    {platformTab === 'angular' && (
                      <>
                        <h5 className="mb-3">Angular Integration</h5>
                        <p className="text-muted">
                          For Angular applications, add the script to index.html and use Router events for tracking.
                        </p>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 1: Add Script to index.html</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(integrationData.integration_code, 'Script code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded">
                            <pre className="mb-0 text-light">
                              <code>{integrationData.integration_code}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Step 2: Track Route Changes (app.component.ts)</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(`import { Component } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window { cloudayn: any; }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent {
  constructor(private router: Router) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (window.cloudayn) {
        window.cloudayn('pageview', {
          path: event.urlAfterRedirects,
          title: document.title
        });
      }
    });
  }
}`, 'Angular code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            <pre className="mb-0 text-light">
                              <code>{`import { Component } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window { cloudayn: any; }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent {
  constructor(private router: Router) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (window.cloudayn) {
        window.cloudayn('pageview', {
          path: event.urlAfterRedirects,
          title: document.title
        });
      }
    });
  }
}`}</code>
                            </pre>
                          </div>
                        </div>
                      </>
                    )}

                    {/* WordPress Integration */}
                    {platformTab === 'wordpress' && (
                      <>
                        <h5 className="mb-3">WordPress Integration</h5>
                        <p className="text-muted">
                          For WordPress sites, you can add the tracking code via your theme or a plugin.
                        </p>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Option 1: Add to functions.php</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(`// Add this to your theme's functions.php
function add_cloudayn_tracking() {
  ?>
  ${integrationData.integration_code}
  <?php
}
add_action('wp_head', 'add_cloudayn_tracking');`, 'WordPress functions.php code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                            <pre className="mb-0 text-light">
                              <code>{`// Add this to your theme's functions.php
function add_cloudayn_tracking() {
  ?>
  ${integrationData.integration_code}
  <?php
}
add_action('wp_head', 'add_cloudayn_tracking');`}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="form-label fw-semibold">Option 2: Use a Plugin</label>
                          <div className="bg-light p-3 rounded border">
                            <ol className="mb-0 ps-3">
                              <li className="mb-2">Install a header/footer script plugin (e.g., "Insert Headers and Footers")</li>
                              <li className="mb-2">Go to <strong>Settings → Insert Headers and Footers</strong></li>
                              <li className="mb-2">Paste the integration code in the "Header" section</li>
                              <li>Save changes</li>
                            </ol>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Integration Code</label>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => copyToClipboard(integrationData.integration_code, 'Integration code')}
                            >
                              <i className="bi bi-clipboard me-2"></i>
                              Copy Code
                            </button>
                          </div>
                          <div className="bg-dark text-light p-3 rounded">
                            <pre className="mb-0 text-light">
                              <code>{integrationData.integration_code}</code>
                            </pre>
                          </div>
                        </div>

                        <div className="alert alert-warning mb-0">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <strong>Child Theme Recommended:</strong> If editing functions.php, use a child theme to prevent losing changes during theme updates.
                        </div>
                      </>
                    )}
                  </div>
  );
};

export default TrackingSnippetTabs;
